import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  Observable,
  Subject,
  of,
  shareReplay,
  BehaviorSubject,
  firstValueFrom,
} from "rxjs";
import { CustomerInformation } from "src/models/customer";
import { ActivatedRoute, Router } from "@angular/router";
import { ApiService } from "src/services/api.service";
import { catchError, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { faAngleDown, faCircle } from "@fortawesome/free-solid-svg-icons";
import { AppSettingsService } from "../app-settings/app-settings.service";
import { CustomerStateService } from "src/services/customer-state.service";
import { MapGeocoder } from "@angular/google-maps";
import { HttpClient } from "@angular/common/http";
import { SiteInformation, ModalSiteInfo } from "src/models/site-information";
import { LocationInfo } from "src/models/location-info";

@Component({
  selector: "app-landing",
  templateUrl: "./landing.component.html",
  styleUrls: ["./landing.component.scss"],
  providers: [],
})
export class LandingPageComponent implements OnInit, OnDestroy, AfterViewInit {
  faAngleDown = faAngleDown;
  faCircle = faCircle;

  private readonly zipPermittedKeys = new Array<string>(
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "Backspace",
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown"
  );

  public customerInformation: CustomerInformation = null;
  public customerInformation$ = new Observable<CustomerInformation>(null);

  public sites$: Observable<SiteInformation[]>;
  private _modalSites = new BehaviorSubject<ModalSiteInfo[]>(null);
  public modalSites$ = this._modalSites.asObservable();
  public zipCode: string;
  public invalidZip: boolean = false;
  selectedIndex: number = -1;
  public customerId: string;
  public siteId: string;

  private readonly onDestroy$ = new Subject<void>();

  public primaryColor: string;
  public primaryTextColor: string;
  public neutralBgColor: string;
  public secondaryBgColor: string;
  public secondaryTextColor: string;
  public canContinue: boolean = false;

  private readonly mapsApiKey: string;

  public info: LocationInfo = { lat: 0, lon: 0 };

  public isProduction: boolean = false;

  public innerWidth: number = window.innerWidth;

  constructor(
    private apiService: ApiService,
    public customerStateService: CustomerStateService,
    private router: Router,
    private route: ActivatedRoute,
    public geocoder: MapGeocoder,
    private http: HttpClient,
    private appSettingsService: AppSettingsService,
    private host: ElementRef<HTMLElement>
  ) {
    this.mapsApiKey = `${this.appSettingsService.googleMapsKey}`;
    this.isProduction = this.appSettingsService.isProduction;
  }

  async ngOnInit() {
    const customerId$ = this.route.params.pipe(map((t) => t.customerId));
    sessionStorage.clear();
    customerId$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (customerId) => (this.customerStateService.customerId = customerId)
      );
  }

  onKeyDown(event: KeyboardEvent) {
    let key = event.key;
    if (!this.zipPermittedKeys.includes(key)) {
      event.preventDefault();
    }
  }

  async updateZip() {
    if (this.zipCode.length >= 4) {
      // Provide geocoder with zip code to get coordinates
      this.geocoder
        .geocode({
          address: this.zipCode,
        })
        .pipe(
          map((response) => {
            return {
              lat: response.results[0].geometry.location.lat(),
              lon: response.results[0].geometry.location.lng(),
            };
          }),
          switchMap(
            (
              coords // Use coordinates to get sites from API
            ) => this.apiService.getModalSites(coords.lat, coords.lon)
          )
        )
        .subscribe({
          next: (modalSites) => {
            // Set sites list using API response and clear invalid zip message
            this.invalidZip = false;
            this._modalSites.next(modalSites);
          },
          error: (err) => {
            // Handle errors with invalid zip message and null sites list
            // console.error(err);
            this.invalidZip = true;
            this._modalSites.next(null);
          },
        });
    } else {
      this.invalidZip = false;
      this._modalSites.next(null);
    }
  }

  ngAfterViewInit() {
    this.http
      .jsonp(
        "https://maps.googleapis.com/maps/api/js?key=" + this.mapsApiKey,
        "callback"
      )
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  CloseModal() {
    const modalDiv = document.getElementById("wash-select-modal");
    if (modalDiv != null) {
      modalDiv.style.display = "none";
    }
  }

  formatPhone(number: string) {
    return this.customerStateService.formatPhone(number);
  }

  async washPassButtonClick() {
    //If not loaded
    //open the modal.
    const modalDiv = document.getElementById("wash-select-modal");
    const modalBg = document.getElementById("modal-background");
    if (modalDiv != null) {
      modalDiv.style.display = "block";
      modalBg.style.position = "fixed";
      modalBg.style.top = "0";
      modalBg.style.bottom = "0";
      modalBg.style.right = "0";
      modalBg.style.left = "0";
      modalBg.style.backgroundColor = "#000";
      modalBg.style.opacity = "0.75";
      modalBg.style.zIndex = "-1";
    }
    //show the closest 5 sites. (api call.)
    //android app uses this api call.
    //pick a site and then re-navigate to the landing page
    //this time with the customer id of the site selected.
  }

  toggleBackground(index: number): void {
    this.selectedIndex = index;
  }

  contactButtonClick() {
    this.router.navigate(["contact-us"]);
  }

  termsButtonClick() {
    this.router.navigate(["terms-of-service"]);
  }

  setNewActiveSiteClick(siteId: string, customerId: string) {
    // this.customerStateService.forceSetCurrentActiveSite(siteId);
    this.siteId = siteId;
    this.customerId = customerId;

    this.canContinue = true;
  }

  redirectToCustLandingPage() {
    // this.customerStateService.setCurrentSite();
    sessionStorage.setItem("siteId", this.siteId);
    this.router.navigate(["customer", this.customerId]);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }
}
